<template>
	<div class="sidebar-thread-wrapper" :class="{ 'is-last': isLast }">
		<RouterLink
			class="drawer-item-thread drawer-item"
			draggable="false"
			:class="{
				[`thread-${thread.id}`]: true,
				'menu-open': $refs.menu?.$refs?.menu?.isOpen,
				active: isActive,
				visible: isVisible,
			}"
			:to="linkTo"
			role="button"
			tabindex="-1"
		>
			<!-- Tree connector SVG -->
			<svg
				class="tree-connector"
				viewBox="0 0 15 42"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<!-- Vertical trunk -->
				<line
					x1="1"
					y1="0"
					x2="1"
					:y2="isLast ? 17 : 44"
					stroke="currentColor"
					stroke-width="2"
				/>
				<!-- Horizontal branch with rounded corner -->
				<path
					d="M1 17 Q1 21 5 21 L15 21"
					stroke="currentColor"
					stroke-width="2"
					stroke-linecap="round"
					fill="none"
				/>
			</svg>

			<div class="drawer-item-content">
				<div class="drawer-item-title">
					<span
						class="drawer-item-title-text"
						:class="{ unread: hasUnread }"
						v-text="thread.title || thread.displayname"
					/>
				</div>
			</div>

			<div class="drawer-item-actions">
				<EyeSlashIcon type="solid" v-if="isHidden" class="hidden-icon" />
				<BellSlashIcon type="solid" v-if="isMuted" class="muted-icon" />

				<UnreadBadge
					v-if="showBadge"
					class="drawer-item-badge"
					:class="{ 'non-mentions': !isMarkedUnread && !mentionsCount }"
					:unread="mentionsCount || (isMarkedUnread ? 0 : unreadCount)"
					showDot
				/>

				<DotsMenu
					v-if="hasMembership"
					class="drawer-item-menu"
					localize
					:items="menuOptions"
					@select="onMenuItemClick"
					ref="menu"
				/>
			</div>
		</RouterLink>

		<StandardModal
			v-if="showMaxPinnedModal"
			small
			open
			:title="$t('common_max_limit')"
			:confirmText="$t('common_button_close')"
			@confirm="showMaxPinnedModal = false"
			@cancel="showMaxPinnedModal = false"
			:confirmProps="{ danger: true, loading: isLoading }"
		>
			<div class="drawer-modal-content max-pinned">
				<CircleExclamationIcon halo />
				<div
					class="modal-title"
					v-text="
						$t('chat_drawer_section_pinned_limit', { Count: MAX_PINNED_CHATS })
					"
				/>
			</div>
		</StandardModal>
	</div>
</template>

<script>
import store, {
	getMyMembershipForBubble,
	setMembership,
	getPinnedChats,
	updateStat,
	getStat,
	getEffectiveMentions,
} from '@/store'
import { UnreadBadge, DotsMenu, StandardModal } from '@/components/common'
import { MAX_PINNED_CHATS } from '@/helpers/constants'
import {
	BellSlashIcon,
	BellIcon,
	MessageCheckIcon,
	MessageDotIcon,
	ArrowRightToBracketIcon,
	ThumbtackSlashIcon,
	ThumbtackIcon,
	CircleExclamationIcon,
	EyeIcon,
	EyeSlashIcon,
} from '@/icons'
import { post } from '@/helpers/api'
import i18n from '@/i18n'
import { getErrorText } from '@/helperMethods'
import { markChatAsRead, markChatAsUnread } from '@/api/membership'
import scrollIntoView from 'scroll-into-view-if-needed'
import { closeNotificationsForBubble } from '@/helpers/pushNotification'

export default {
	name: 'SidebarThread',
	props: {
		thread: Object,
		isVisible: Boolean,
		isLast: Boolean,
	},
	components: {
		UnreadBadge,
		DotsMenu,
		StandardModal,
		BellSlashIcon,
		EyeSlashIcon,
		CircleExclamationIcon,
	},

	data() {
		return {
			showMaxPinnedModal: false,
			isLoading: false,
			MAX_PINNED_CHATS,
		}
	},

	computed: {
		isActive() {
			const { name, params } = this.$route
			// Only highlight in fullscreen mode (/chat/{threadId}), not when
			// viewing as a sub-route (/chat/{parentId}/thread/{threadId})
			// where the parent SidebarChat handles the highlight.
			if (name === 'chat' && parseInt(params.id) === this.thread.id) return true
			return false
		},
		hasMembership() {
			return !!getMyMembershipForBubble(this.thread.id)
		},
		isMuted() {
			return this.thread.mute ?? false
		},
		unreadCount() {
			return this.thread.unread ?? 0
		},
		mentionsCount() {
			return getEffectiveMentions(this.thread)
		},
		isMarkedUnread() {
			return this.thread.marked_unread
		},
		hasUnread() {
			return (
				this.unreadCount > 0 || this.mentionsCount > 0 || this.isMarkedUnread
			)
		},
		showBadge() {
			return (
				this.unreadCount > 0 || this.isMarkedUnread || this.mentionsCount > 0
			)
		},
		isPinned() {
			return this.thread.is_pinned ?? false
		},
		isHidden() {
			return this.thread.ishidden ?? false
		},
		menuOptions() {
			return [
				{
					id: this.hasUnread ? 'markasread' : 'markasunread',
					label: this.hasUnread
						? 'chat_drawer_action_markread'
						: 'chat_drawer_action_markunread',
					icon: this.hasUnread ? MessageCheckIcon : MessageDotIcon,
				},
				{
					id: this.isPinned ? 'unpin' : 'pin',
					label: this.isPinned
						? 'chat_drawer_action_unpin'
						: 'chat_drawer_action_pin',
					icon: this.isPinned ? ThumbtackSlashIcon : ThumbtackIcon,
				},
				{
					id: this.isMuted ? 'unmute' : 'mute',
					label: this.isMuted
						? 'chat_drawer_action_unmute'
						: 'chat_drawer_action_mute',
					icon: this.isMuted ? BellIcon : BellSlashIcon,
				},
				{
					id: this.isHidden ? 'unhide' : 'hide',
					label: this.isHidden
						? 'chat_drawer_action_unhide'
						: 'chat_drawer_action_hide',
					icon: this.isHidden ? EyeIcon : EyeSlashIcon,
				},
				{
					id: 'leave',
					label: 'chat_drawer_action_leave',
					danger: true,
					icon: ArrowRightToBracketIcon,
				},
			].map((x) => {
				x.iconProps = x.iconProps || {}
				x.iconProps.type = x.iconProps.type || 'regular'
				return x
			})
		},
		linkTo() {
			return { name: 'chat', params: { id: this.thread.id, chat: 'chat' } }
		},
	},

	methods: {
		onMenuItemClick({ id }) {
			switch (id) {
				case 'markasread':
					return this.markAsRead()
				case 'markasunread':
					return this.markAsUnread()
				case 'pin':
					return this.saveIsPinned(true)
				case 'unpin':
					return this.saveIsPinned(false)
				case 'mute':
					return this.muteThread()
				case 'unmute':
					return this.unmuteThread()
				case 'hide':
					return this.hideThread()
				case 'unhide':
					return this.unhideThread()
				case 'leave':
					return this.leaveThread()
			}
		},

		async markAsRead() {
			const { latest_message_id, mark, unread, unread_mentions } = getStat(
				this.thread.id
			)

			if (this.isMarkedUnread) {
				try {
					await markChatAsRead(this.thread.id)
				} catch (e) {
					this.$alert.error(e)
				}
			}

			updateStat(this.thread.id, {
				unread: 0,
				unread_mentions: 0,
			})

			try {
				const { data } = await post('/api/v1/bubble.mark', {
					bubble_id: this.thread.id,
					message_id: latest_message_id,
				})
				if (data.ok) {
					updateStat(this.thread.id, {
						unread: 0,
						unread_mentions: 0,
						mark: latest_message_id,
					})
					closeNotificationsForBubble(this.thread.id)
				} else {
					updateStat(this.thread.id, {
						unread,
						unread_mentions,
						mark,
					})
				}
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
				updateStat(this.thread.id, {
					unread,
					unread_mentions,
				})
			}
		},

		async markAsUnread() {
			try {
				await markChatAsUnread(this.thread.id)
			} catch (e) {
				this.$alert.error(e)
			}
		},

		async muteThread() {
			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: this.thread.id,
					mute: true,
				})
				setMembership(data.membership)
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
			}
		},

		async unmuteThread() {
			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: this.thread.id,
					mute: false,
					muteuntil: null,
				})
				setMembership(data.membership)
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
			}
		},

		async saveIsPinned(is_pinned) {
			if (is_pinned) {
				const pinnedCount = getPinnedChats().length
				if (pinnedCount >= MAX_PINNED_CHATS) {
					this.showMaxPinnedModal = true
					return
				}
			}

			this.isLoading = true

			const membership = getMyMembershipForBubble(this.thread.id)
			membership.is_pinned = is_pinned
			setMembership(membership)

			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: this.thread.id,
					is_pinned,
				})
				setMembership(data.membership)

				if (is_pinned) {
					EventBus.$emit('expand-category', 'pinned')
					const elem = document.querySelector(
						`.pinned .drawer-item-chat.chat-${this.thread.id}`
					)
					if (elem) {
						scrollIntoView(elem, {
							scrollMode: 'if-needed',
							block: 'center',
							inline: 'nearest',
							behavior: 'smooth',
						})
					}
				}
			} catch (e) {
				console.error(e)
				membership.is_pinned = !is_pinned
				setMembership(membership)
			}
			this.isLoading = false
		},

		async hideThread() {
			const membership = getMyMembershipForBubble(this.thread.id)
			membership.ishidden = true
			setMembership(membership)

			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: this.thread.id,
					ishidden: 1,
				})
				if (!data.ok) {
					const key = getErrorText(data.error, 'chat_drawer_section_error_hide')
					throw new Error(
						i18n.global.t(key, {
							GroupName: this.thread.title || this.thread.displayname,
						})
					)
				}
				setMembership(data.membership)
			} catch (e) {
				console.error(e)
				membership.ishidden = false
				setMembership(membership)
				this.$alert.error(e)
			}
		},

		async unhideThread() {
			const membership = getMyMembershipForBubble(this.thread.id)
			membership.ishidden = false
			setMembership(membership)

			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: this.thread.id,
					ishidden: 0,
				})
				if (!data.ok) {
					const key = getErrorText(
						data.error,
						'chat_drawer_section_error_unhide'
					)
					throw new Error(
						i18n.global.t(key, {
							GroupName: this.thread.title || this.thread.displayname,
						})
					)
				}
				setMembership(data.membership)
			} catch (e) {
				console.error(e)
				membership.ishidden = true
				setMembership(membership)
				this.$alert.error(e)
			}
		},

		async leaveThread() {
			try {
				await post('/api/v1/bubble.leave', {
					bubble_id: this.thread.id,
				})
				// Thread will be removed from store automatically
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$item-height: 42px;
$gap: 8px;
$indent: 54px; // 11px gap between tree branch end and thread text

.sidebar-thread-wrapper {
	position: relative;

	.tree-connector {
		position: absolute;
		left: 27px;
		top: 0;
		width: 15px;
		height: 100%;
		color: var(--neutral-300);
	}
}

.drawer-modal-content {
	@include flex-column-center;
	justify-content: space-around;
	height: 100%;
	padding: 16px 40px;
	text-align: center;
	&.max-pinned {
		.pronto-icon {
			color: var(--red-200);
		}
	}
	.pronto-icon {
		font-size: 45px;
		color: var(--blue-300);
		margin-bottom: 16px;
	}
	.modal-title {
		@include fontFamily(500);
		margin-bottom: 8px;
	}
}

.drawer-item-thread {
	position: relative;
	width: 100%;
	height: $item-height;
	padding: 0 16px 0 $indent;
	@include flex($gap);
	color: inherit;
	text-decoration: none;

	// Show menu on hover for all states (active and non-active)
	&:hover .drawer-item-menu {
		width: 20px !important;
		opacity: 1 !important;
	}

	&:hover:not(.active) {
		&::before {
			content: '';
			position: absolute;
			left: 44px;
			right: 11px;
			top: 0;
			bottom: 0;
			background: var(--neutral-200);
			border-radius: 8px;
			z-index: 0;
		}
		.drawer-item-menu {
			width: 20px !important;
			opacity: 1 !important;
		}
	}

	&:focus:not(.active) {
		&::before {
			content: '';
			position: absolute;
			left: 44px;
			right: 11px;
			top: 0;
			bottom: 0;
			background: var(--neutral-200);
			border-radius: 8px;
			z-index: 0;
		}
		.drawer-item-menu {
			width: 20px !important;
			opacity: 1 !important;
		}
	}

	&.menu-open:not(.active) {
		&::before {
			content: '';
			position: absolute;
			left: 44px;
			right: 11px;
			top: 0;
			bottom: 0;
			background: var(--neutral-200);
			border-radius: 8px;
			z-index: 0;
		}
		.drawer-item-menu {
			width: 20px !important;
			opacity: 1 !important;
			:deep(.actions) {
				background: var(--neutral-300) !important;
			}
		}
	}
	&.menu-open.active {
		:deep(.actions) {
			background: var(--teal-200) !important;
		}
	}

	&.active {
		&::before {
			content: '';
			position: absolute;
			left: 44px;
			right: 11px;
			top: 0;
			bottom: 0;
			background: var(--teal-300);
			border-radius: 8px;
			z-index: 0;
		}
		.pronto-icon {
			color: var(--neutral-0) !important;
		}
		.drawer-item-content {
			color: var(--neutral-0) !important;
		}
		.thread-branch {
			border-color: var(--neutral-0);
		}
		.drawer-item-actions {
			.drawer-item-menu {
				:deep(.pronto-icon) {
					color: var(--neutral-0) !important;
				}
				:deep(.actions) {
					&:hover {
						background: var(--teal-200) !important;
					}
				}
			}
		}
	}

	.drawer-item-content {
		flex: 1 1 0%;
		min-width: 0;
		@include flex-column;
		overflow: hidden;
		font-size: 14px;
		color: var(--neutral-800);
		position: relative;
		z-index: 1;

		.drawer-item-title {
			@include flex($gap);
			width: max-content;
			max-width: 100%;
			.drawer-item-title-text {
				@include ellipsis;
				&.unread {
					@include fontFamily(900);
				}
			}
		}
	}

	.drawer-item-actions {
		flex: 0 0 auto;
		@include flex($gap);
		margin-left: auto;
		position: relative;
		z-index: 1;
		.pronto-icon {
			color: var(--neutral-500);
		}
		:deep(.pronto-icon) {
			color: var(--neutral-500);
		}
		.muted-icon {
			font-size: 14px;
		}
		.drawer-item-badge {
			&.non-mentions {
				color: var(--neutral-500);
				background: var(--neutral-0);
			}
		}
		.drawer-item-menu {
			width: 0;
			opacity: 0;
			overflow: hidden;
			transition: all ease-in-out 100ms;
			transition-property: width, opacity;
			:deep(.actions) {
				width: 20px;
				height: 25px;
				overflow: hidden;
				padding: 0;
				border-radius: 3px;
				font-size: 18px;
				&:hover {
					background: var(--neutral-300) !important;
				}
			}
			&:focus {
				width: 20px !important;
				opacity: 1 !important;
			}
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.drawer-item-thread {
		&.active {
			&::before {
				background: var(--neutral-300) !important;
			}
			.drawer-item-content {
				color: white !important;
			}
			.thread-branch {
				border-color: var(--neutral-500);
			}
			.drawer-item-actions {
				.drawer-item-menu {
					.pronto-icon {
						color: var(--neutral-800) !important;
					}
					.actions {
						background: var(--neutral-0) !important;
						&:hover {
							background: var(--neutral-0) !important;
						}
					}
				}
			}
		}
		.drawer-item-actions {
			.drawer-item-badge {
				&.non-mentions {
					color: var(--neutral-800) !important;
					background: var(--neutral-500) !important;
				}
			}
		}
	}
}
</style>
